import { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

const flattenEvidencesFromQueryData = (data: unknown): any[] => {
  if (!data || typeof data !== "object") {
    return [];
  }

  const queryData = data as {
    pages?: Array<{ evidences?: any[] }>;
    evidences?: any[];
  };

  if (Array.isArray(queryData.pages)) {
    return queryData.pages.flatMap((page) => page?.evidences || []);
  }

  return queryData.evidences || [];
};

const evidenceIdMatches = (evidence: { _id?: unknown }, idToFind: string): boolean =>
  String(evidence._id) === String(idToFind);

/**
 * Hook to manage which evidence is selected via URL parameters
 * This ONLY manages URL state (evidenceId, requestId) and reads evidence data
 * It does NOT control the drawer open/close state
 */
export const useEvidenceSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: ideaId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  
  // Get evidenceId and requestId from URL parameters
  const evidenceId = searchParams.get('evidenceId');
  const requestId = searchParams.get('requestId');
  const fromSearch = searchParams.get('fromSearch') === 'true';
  
  // Track if the current evidence selection came from the initial URL load
  // This is true when the page loads with an evidenceId/requestId in the URL
  // It becomes false once the user clicks on a different evidence (via selectEvidenceById)
  const [isFromUrlNavigation, setIsFromUrlNavigation] = useState(Boolean(evidenceId || requestId));
  
  // Helper to get the most recently updated query of a specific type
  const getLatestQueryByType = useCallback((queryType: string) => {
    const allCachedData = queryClient.getQueryCache().getAll();
    const candidates = allCachedData.filter(query => {
      const queryKey = query.queryKey;
      return Array.isArray(queryKey) && 
             queryKey.length >= 4 &&
             queryKey[0] === 'idea' &&
             queryKey[1] === 'detail' &&
             queryKey[2] === ideaId &&
             queryKey[3] === queryType;
    });
    if (candidates.length === 0) {
      return undefined;
    }
    return candidates.reduce((latest, current) => {
      const latestAt = (latest?.state as any)?.dataUpdatedAt || 0;
      const currentAt = (current?.state as any)?.dataUpdatedAt || 0;
      return currentAt > latestAt ? current : latest;
    }, candidates[0]);
  }, [queryClient, ideaId]);

  // Get the filtered evidences query to subscribe to its changes (for backward compatibility)
  const getFilteredEvidencesQuery = useCallback(() => {
    return getLatestQueryByType('filtered-evidences');
  }, [getLatestQueryByType]);

  // Get evidence data from React Query cache
  const getAllEvidences = useCallback(() => {
    const assignedQuery =
      getLatestQueryByType('assigned-evidences-infinite') ||
      getLatestQueryByType('assigned-evidences');
    const suggestedQuery =
      getLatestQueryByType('suggested-evidences-infinite') ||
      getLatestQueryByType('suggested-evidences');
    const additionalQuery = getLatestQueryByType('additional-evidences');

    if (assignedQuery?.state?.data || suggestedQuery?.state?.data || additionalQuery?.state?.data) {
      const assignedEvidences = flattenEvidencesFromQueryData(assignedQuery?.state?.data);
      const suggestedEvidences = flattenEvidencesFromQueryData(suggestedQuery?.state?.data);
      const additionalEvidences = flattenEvidencesFromQueryData(additionalQuery?.state?.data);

      return [...assignedEvidences, ...suggestedEvidences, ...additionalEvidences];
    }

    // Fallback: check for filtered-evidences query (old approach)
    const filteredEvidencesQuery = getFilteredEvidencesQuery();

    if (filteredEvidencesQuery?.state?.data) {
      const filteredData = filteredEvidencesQuery.state.data as any;
      const evidences = filteredData?.evidences || [];
      const suggestedEvidences = filteredData?.suggestedEvidences || [];
      const additionalEvidences = filteredData?.additionalEvidences || filteredData?.searchedEvidences || [];

      return [...evidences, ...suggestedEvidences, ...additionalEvidences];
    }
    
    // Fallback: check for legacy cache keys
    const evidencesData = queryClient.getQueryData(['idea-evidences', ideaId]);
    const matchesData = queryClient.getQueryData(['idea-matches', ideaId]);
    
    const legacyEvidences = (evidencesData as any)?.evidences || [];
    const legacyMatches = (matchesData as any)?.evidences || (matchesData as any)?.matches || [];
    
    return [...legacyEvidences, ...legacyMatches];
  }, [queryClient, ideaId, getFilteredEvidencesQuery, getLatestQueryByType]);
  
  // Track the query data state to trigger re-renders when it changes
  // Check all query types to get the latest update time
  const assignedQuery =
    getLatestQueryByType('assigned-evidences-infinite') ||
    getLatestQueryByType('assigned-evidences');
  const suggestedQuery =
    getLatestQueryByType('suggested-evidences-infinite') ||
    getLatestQueryByType('suggested-evidences');
  const additionalQuery = getLatestQueryByType('additional-evidences');
  const filteredEvidencesQuery = getFilteredEvidencesQuery();
  
  // Get the latest dataUpdatedAt from any of the queries
  const queryDataHash = Math.max(
    assignedQuery?.state?.dataUpdatedAt || 0,
    suggestedQuery?.state?.dataUpdatedAt || 0,
    additionalQuery?.state?.dataUpdatedAt || 0,
    filteredEvidencesQuery?.state?.dataUpdatedAt || 0
  );
  
  // Check if we're waiting for evidence to load
  const isLoadingEvidence = useMemo(() => {
    const allEvidences = getAllEvidences();
    const idToFind = evidenceId || requestId;
    
    if (idToFind) {
      if (!allEvidences || allEvidences.length === 0) {
        return true;
      }
      const foundEvidence = allEvidences.find((evidence: any) => evidenceIdMatches(evidence, idToFind));
      if (!foundEvidence) {
        return true;
      }
    }
    return false;
  }, [evidenceId, requestId, getAllEvidences, queryDataHash]);

  const previousChosenRef = useRef<any>(null);

  // Find chosen evidence based on URL evidenceId/requestId
  const chosenEvidence = useMemo(() => {
    const allEvidences = getAllEvidences();
    
    if (!allEvidences) {
      previousChosenRef.current = null;
      return null;
    }
    
    const idToFind = evidenceId || requestId;
    
    if (!idToFind) {
      previousChosenRef.current = null;
      return null;
    }
    
    const foundEvidence = allEvidences.find((evidence: any) => evidenceIdMatches(evidence, idToFind));
    if (!foundEvidence) {
      previousChosenRef.current = null;
      return null;
    }

    const previous = previousChosenRef.current;
    let merged = { ...foundEvidence };
    if (previous && evidenceIdMatches(previous, idToFind)) {
      for (const key of Object.keys(previous)) {
        if (merged[key] === undefined) {
          merged[key] = previous[key];
        }
      }
    }
    if (fromSearch) {
      merged = { ...merged, status: "search" };
    }
    previousChosenRef.current = merged;
    return merged;
  }, [evidenceId, requestId, getAllEvidences, fromSearch, queryDataHash]);

  // Select evidence by ID - updates URL only
  // When called programmatically (user click), it marks the selection as NOT from URL navigation
  const selectEvidenceById = useCallback((evidenceId: string | null, fromSearch?: boolean) => {
    // Mark that this selection is from a user click, not from URL navigation
    // This is used to determine if we should scroll to the evidence
    setIsFromUrlNavigation(false);
    
    // Preserve ALL existing URL params (including filters) by constructing from string
    const newSearchParams = new URLSearchParams(searchParams.toString());
    
    if (evidenceId) {
      newSearchParams.set('evidenceId', evidenceId);
      newSearchParams.set('fromSearch', fromSearch ? 'true' : 'false');
      newSearchParams.delete('requestId');
    } else {
      newSearchParams.delete('evidenceId');
      newSearchParams.delete('requestId');
      newSearchParams.delete('fromSearch');
    }
    
    navigate({ search: newSearchParams.toString() }, { replace: true });
  }, [navigate, searchParams]);

  // Clear evidence selection from URL
  const clearSelection = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete('evidenceId');
    newSearchParams.delete('requestId');
    newSearchParams.delete('fromSearch');
    navigate({ search: newSearchParams.toString() }, { replace: true });
  }, [navigate, searchParams]);

  // Effect to handle requestId conversion to evidenceId when evidences are loaded
  useEffect(() => {
    const idToFind = evidenceId || requestId;
    
    if (idToFind) {
      const allEvidences = getAllEvidences();
      
      if (allEvidences && allEvidences.length > 0) {
        const foundEvidence = allEvidences.find((evidence: any) => evidenceIdMatches(evidence, idToFind));
        
        if (foundEvidence && requestId && !evidenceId) {
          // Convert requestId to evidenceId
          const newSearchParams = new URLSearchParams(searchParams.toString());
          newSearchParams.set('evidenceId', requestId);
          newSearchParams.delete('requestId');
          navigate({ search: newSearchParams.toString() }, { replace: true });
        }
      }
    }
  }, [requestId, evidenceId, getAllEvidences, searchParams, navigate, queryDataHash]);

  return {
    // URL State
    evidenceId,
    requestId,
    fromSearch,
    isFromUrlNavigation,
    
    // Evidence Data
    chosenEvidence,
    isLoadingEvidence,
    getAllEvidences,
    
    // URL Actions (these preserve all other URL params including filters)
    selectEvidenceById,
    clearSelection,
  };
};

